import { useState } from "react";
import { Input } from "@/components/ui/input";
import { PageHeader, Loading, ExportButton } from "@/components/Common";
import { FilterBar, SimpleSelect, useRowFilter } from "@/components/Filters";
import { QuickEntryDialog } from "@/components/QuickEntryDialog";
import { useLoad } from "@/hooks/useData";
import { usePref } from "@/hooks/usePref";
import { api } from "@/lib/api";
import { monthStr, monthLabel } from "@/lib/format";

const DayCell = ({ d, mode, onClick }) => {
  const [i, o, c] = d;
  return (
    <td onClick={onClick} className="!px-1 !py-1 text-center font-mono text-[11px] leading-tight cursor-pointer hover:bg-primary/10 border-l border-border/40 min-w-[44px]">
      {(mode === "all" || mode === "in") && <div className={i ? "text-emerald-400 font-bold" : "text-slate-300"}>{i ? `+${i}` : "·"}</div>}
      {(mode === "all" || mode === "out") && <div className={o ? "text-red-400 font-bold" : "text-slate-300"}>{o ? `-${o}` : "·"}</div>}
      {(mode === "all" || mode === "close") && <div className={c < 0 ? "text-red-600" : "text-slate-600"}>{c}</div>}
    </td>
  );
};

export default function Ledger() {
  const [month, setMonth] = useState(monthStr());
  const [warehouse, setWarehouse] = usePref("ledger-warehouse", "all");
  const [mode, setMode] = usePref("ledger-mode", "all");
  const [entry, setEntry] = useState({ open: false, defaults: {} });
  const { data } = useLoad(() => api.get("/report/ledger", { params: { month, warehouse } }).then((r) => r.data), [month, warehouse]);
  const f = useRowFilter(data?.rows);

  const openEntry = (r, day) => setEntry({ open: true, defaults: {
    product_id: r.id, date: `${month}-${String(day).padStart(2, "0")}`, warehouse: warehouse === "B" ? "B" : "A" } });

  return (
    <div>
      <PageHeader title="Daily Stock Ledger" subtitle={`Day-wise IN / OUT / closing for ${monthLabel(month)} — click any day cell to add an entry.`}>
        <ExportButton kind="ledger" params={{ month, warehouse }} />
      </PageHeader>
      <div className="mb-3 flex flex-wrap gap-2">
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-9 w-44" data-testid="ledger-month-input" />
        <SimpleSelect value={warehouse} onChange={setWarehouse} testid="ledger-warehouse-select" options={[["all", "All warehouses"], ["A", "Warehouse A"], ["B", "Warehouse B"]]} />
        <SimpleSelect value={mode} onChange={setMode} testid="ledger-mode-select" options={[["all", "IN / OUT / Close"], ["in", "IN only"], ["out", "OUT only"], ["close", "Closing only"]]} />
      </div>
      {!data ? <Loading /> : (
        <>
          <FilterBar f={f} total={data.rows.length} />
          <div className="panel overflow-auto max-h-[calc(100vh-300px)]">
            <table className="data-table" data-testid="ledger-table">
              <thead>
                <tr>
                  <th className="sticky left-0 z-20 bg-card min-w-[240px]">Product</th>
                  <th className="text-right">Opening</th>
                  {Array.from({ length: data.days }, (_, i) => <th key={i} className="!px-1 text-center border-l border-border/40">D{i + 1}</th>)}
                  <th className="text-right text-emerald-400">IN</th><th className="text-right text-red-400">OUT</th><th className="text-right">Closing</th>
                </tr>
              </thead>
              <tbody>
                {f.filtered.map((r) => (
                  <tr key={r.id} data-testid={`ledger-row-${r.sku}`}>
                    <td className="sticky left-0 z-[5] bg-card max-w-[260px]"><div className="truncate text-xs font-medium" title={r.name}>{r.name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.sku}</div></td>
                    <td className="num">{r.opening}</td>
                    {r.days.map((d, i) => <DayCell key={i} d={d} mode={mode} onClick={() => openEntry(r, i + 1)} />)}
                    <td className="num text-emerald-400 font-bold">{r.total_in}</td>
                    <td className="num text-red-400 font-bold">{r.total_out}</td>
                    <td className="num font-bold" data-testid={`ledger-closing-${r.sku}`}>{r.closing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <QuickEntryDialog open={entry.open} defaults={entry.defaults} onOpenChange={(o) => setEntry((s) => ({ ...s, open: o }))} />
    </div>
  );
}
